import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { NavRequestFilters } from '@/types/navigation-report';

const KEYS = {
    projects: 'navrep_projects',
    domains: 'navrep_domains',
    start: 'navrep_start_date',
    end: 'navrep_end_date',
};

/** Última página de entrada aberta (reabre ao voltar à tela; sem parâmetro na URL). */
export const NAV_ENTRY_KEY = 'navrep_entry';
export const NAV_MAX_RANGE_DAYS = 31;
/** Prefixos das queries da tela (usados pelo useIsFetching que trava os filtros). */
export const NAV_QUERY_KEYS = ['navigation_entries', 'navigation_entry_detail'];

const isoDaysAgo = (days: number) => {
    const d = new Date();
    d.setDate(d.getDate() - days);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const readArray = <T,>(key: string): T[] => {
    try {
        const parsed = JSON.parse(localStorage.getItem(key) || '[]');
        return Array.isArray(parsed) ? (parsed as T[]) : [];
    } catch {
        return [];
    }
};

export interface NavigationFiltersState {
    projectIds: number[];
    setProjectIds: (ids: number[]) => void;
    domains: string[];
    setDomains: (domains: string[]) => void;
    startDate: string;
    endDate: string;
    setDates: (start: string, end: string) => void;
    /** Filtros no formato da API (sem limit — quem chama acrescenta). */
    request: NavRequestFilters;
    rangeDays: number;
}

/**
 * Filtros da tela de Navegação, persistidos em localStorage (padrão das telas de
 * relatório): nada passa pela URL. Período padrão: ontem (dia fechado).
 * Trocar de projeto (de fato, não para os mesmos ids) limpa os domínios.
 */
export function useNavigationFilters(): NavigationFiltersState {
    const [projectIds, setProjectIdsState] = useState<number[]>(() => readArray<number>(KEYS.projects));
    const [domains, setDomains] = useState<string[]>(() => readArray<string>(KEYS.domains));
    const [startDate, setStartDate] = useState(() => localStorage.getItem(KEYS.start) || isoDaysAgo(1));
    const [endDate, setEndDate] = useState(() => localStorage.getItem(KEYS.end) || isoDaysAgo(1));

    const projectIdsRef = useRef(projectIds);
    useEffect(() => {
        projectIdsRef.current = projectIds;
    }, [projectIds]);

    useEffect(() => {
        try {
            localStorage.setItem(KEYS.projects, JSON.stringify(projectIds));
            localStorage.setItem(KEYS.domains, JSON.stringify(domains));
            localStorage.setItem(KEYS.start, startDate);
            localStorage.setItem(KEYS.end, endDate);
        } catch {
            /* sem storage */
        }
    }, [projectIds, domains, startDate, endDate]);

    const setProjectIds = useCallback((ids: number[]) => {
        const prev = projectIdsRef.current;
        const same = prev.length === ids.length && prev.every((id, i) => id === ids[i]);
        if (same) return;
        setProjectIdsState(ids);
        setDomains([]);
    }, []);

    const setDates = useCallback((start: string, end: string) => {
        setStartDate(start);
        setEndDate(end);
    }, []);

    const rangeDays = useMemo(() => {
        const s = new Date(`${startDate}T00:00:00`);
        const e = new Date(`${endDate}T00:00:00`);
        return Math.round((e.getTime() - s.getTime()) / 86400000) + 1;
    }, [startDate, endDate]);

    const request = useMemo<NavRequestFilters>(
        () => ({ start: startDate, end: endDate, project_ids: projectIds, domains }),
        [startDate, endDate, projectIds, domains]
    );

    return { projectIds, setProjectIds, domains, setDomains, startDate, endDate, setDates, request, rangeDays };
}

export function readStoredEntry(): string | null {
    try {
        return localStorage.getItem(NAV_ENTRY_KEY) || null;
    } catch {
        return null;
    }
}

export function storeEntry(path: string | null): void {
    try {
        path ? localStorage.setItem(NAV_ENTRY_KEY, path) : localStorage.removeItem(NAV_ENTRY_KEY);
    } catch {
        /* sem storage */
    }
}
